import FeatureGates from '@atlaskit/feature-gate-js-client';
import type {
	AiManagedObjectConfig,
	AiManagedObjectConfigCreateInput,
	AiManagedObjectDesiredUpdateFrequency,
	AiManagedObjectDesiredUpdateFrequencyInput,
	AiManagedObjectSourceInput,
} from '@atlassian/ai-managed-objects/types';

import {
	DEFAULT_SOURCE_MODE,
	getCurrentPageSource,
	getResolvedSourcesFromEntries,
	getSourceEntriesFromSources,
	getSourceUrlsFromEntries,
	getSourcesFromText,
} from '../sources/configuration';

import { DEFAULT_GOAL, isGoal } from './goals';
import { DEFAULT_OUTPUT_FORMAT, getOutputFormatForGoal } from './output-formats';
import type { ManagedBlockConfiguration, ManagedBlockUpdateInterval } from './types';

export const DEFAULT_UPDATE_INTERVAL: ManagedBlockUpdateInterval = 'long';

const UPDATE_INTERVAL_DYNAMIC_CONFIG = 'managed-block-update-intervals';

const UPDATE_INTERVAL_FALLBACK_VALUES: Record<ManagedBlockUpdateInterval, string> = {
	short: 'PT1H',
	medium: 'PT7H',
	long: 'P1D',
};

type UpdateIntervalValues = Record<ManagedBlockUpdateInterval, string>;

const getUpdateIntervalValues = (): UpdateIntervalValues => {
	const config = FeatureGates.getExperiment(UPDATE_INTERVAL_DYNAMIC_CONFIG, {
		fireExperimentExposure: false,
	}).value as Partial<Record<ManagedBlockUpdateInterval, string>>;

	return {
		short: config.short ?? UPDATE_INTERVAL_FALLBACK_VALUES.short,
		medium: config.medium ?? UPDATE_INTERVAL_FALLBACK_VALUES.medium,
		long: config.long ?? UPDATE_INTERVAL_FALLBACK_VALUES.long,
	};
};

export const getDesiredUpdateFrequencyForInterval = (
	updateInterval?: ManagedBlockUpdateInterval,
): AiManagedObjectDesiredUpdateFrequencyInput | undefined => {
	const value = updateInterval ? getUpdateIntervalValues()[updateInterval] : undefined;

	return value ? { type: 'INTERVAL', value } : undefined;
};

export const getUpdateIntervalFromDesiredFrequency = (
	frequency?: AiManagedObjectDesiredUpdateFrequency | null,
): ManagedBlockUpdateInterval | undefined => {
	if (!frequency) {
		return undefined;
	}

	if (frequency.type !== 'INTERVAL') {
		return DEFAULT_UPDATE_INTERVAL;
	}

	const updateInterval = Object.entries(getUpdateIntervalValues()).find(
		([, value]) => value === frequency.value,
	)?.[0] as ManagedBlockUpdateInterval | undefined;

	return updateInterval ?? DEFAULT_UPDATE_INTERVAL;
};

export const getDefaultPanelConfiguration = (): ManagedBlockConfiguration => ({
	goal: DEFAULT_GOAL,
	mode: 'configuration',
	outputFormat: DEFAULT_OUTPUT_FORMAT,
	prompt: '',
	sourceEntries: [],
	sourceMode: DEFAULT_SOURCE_MODE,
	sourceUrls: '',
	updateInterval: DEFAULT_UPDATE_INTERVAL,
});

export const getDefaultStructuredPanelConfiguration = (
	previous: ManagedBlockConfiguration,
): ManagedBlockConfiguration => ({
	...previous,
	goal: DEFAULT_GOAL,
	mode: 'configuration',
	outputFormat: DEFAULT_OUTPUT_FORMAT,
	sourceEntries: [],
	sourceMode: DEFAULT_SOURCE_MODE,
	sourceUrls: '',
});

export const getPanelConfigurationFromAimoConfig = (
	config?: AiManagedObjectConfig | null,
): ManagedBlockConfiguration => {
	if (!config) {
		return getDefaultPanelConfiguration();
	}

	const goal = isGoal(config.goal) ? config.goal : DEFAULT_GOAL;
	const outputFormat = getOutputFormatForGoal(goal, config.goalOperations?.outputFormat);
	const sourceEntries = getSourceEntriesFromSources(config.sources);
	const sourceUrls = getSourceUrlsFromEntries(sourceEntries);

	return {
		goal,
		mode: config.goal ? 'configuration' : 'prompt',
		outputFormat,
		prompt: config.prompt ?? '',
		sourceEntries,
		sourceMode: sourceEntries.length > 0 ? 'manual' : DEFAULT_SOURCE_MODE,
		sourceUrls,
		updateInterval: getUpdateIntervalFromDesiredFrequency(config.desiredUpdateFrequency),
	};
};

export const toAiManagedObjectConfigInput = (
	configuration: ManagedBlockConfiguration,
	currentPageSourceUrl?: string,
): AiManagedObjectConfigCreateInput => {
	const desiredUpdateFrequency = getDesiredUpdateFrequencyForInterval(configuration.updateInterval);

	if (configuration.mode === 'prompt') {
		return {
			desiredUpdateFrequency,
			prompt: configuration.prompt.trim(),
		};
	}

	const goal = configuration.goal;
	const outputFormat = getOutputFormatForGoal(goal, configuration.outputFormat);
	const resolvedSources = getResolvedSourcesFromEntries(configuration.sourceEntries);
	const sources =
		configuration.sourceMode === 'current-page'
			? getCurrentPageSource(currentPageSourceUrl)
			: resolvedSources.length > 0
				? resolvedSources
				: getSourcesFromText(configuration.sourceUrls);

	return {
		desiredUpdateFrequency,
		goal,
		goalOperations: {
			outputFormat,
		},
		prompt: null,
		sources,
	};
};

export const toAiManagedObjectConfigInputWithUpdateInterval = (
	config: AiManagedObjectConfig,
	updateInterval?: ManagedBlockUpdateInterval,
): AiManagedObjectConfigCreateInput => ({
	desiredUpdateFrequency: getDesiredUpdateFrequencyForInterval(updateInterval),
	goal: config.goal ?? undefined,
	goalOperations: config.goalOperations ?? undefined,
	prompt: config.prompt ?? null,
	sources: config.sources?.map((source) => ({ app: source.app, url: source.url })) ?? undefined,
});

type ComparableConfig = {
	desiredUpdateFrequency?: AiManagedObjectDesiredUpdateFrequencyInput;
	goal?: string;
	goalOperations?: Record<string, unknown>;
	prompt: string | null;
	sources?: AiManagedObjectSourceInput[];
};

const normalizeDesiredUpdateFrequency = (
	config?: AiManagedObjectConfigCreateInput | AiManagedObjectConfig | null,
): AiManagedObjectDesiredUpdateFrequencyInput | undefined => {
	if (!config) {
		return getDesiredUpdateFrequencyForInterval(DEFAULT_UPDATE_INTERVAL);
	}

	return config.desiredUpdateFrequency ?? undefined;
};

const normalizeConfig = (
	config?: AiManagedObjectConfigCreateInput | AiManagedObjectConfig | null,
): ComparableConfig => ({
	desiredUpdateFrequency: normalizeDesiredUpdateFrequency(config),
	goal: config?.goal ?? undefined,
	goalOperations: config?.goalOperations ?? undefined,
	prompt: config?.prompt ?? '',
	sources: config?.sources?.map((source) => ({ app: source.app, url: source.url })) ?? undefined,
});

const sortConfigValue = (value: unknown): unknown => {
	if (Array.isArray(value)) {
		return value.map(sortConfigValue);
	}

	if (!value || typeof value !== 'object') {
		return value;
	}

	return Object.fromEntries(
		Object.entries(value)
			.filter(([, entryValue]) => entryValue !== undefined)
			.sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
			.map(([key, entryValue]) => [key, sortConfigValue(entryValue)]),
	);
};

const stableStringify = (value: unknown): string => JSON.stringify(sortConfigValue(value));

const stringifyConfig = (
	config?: AiManagedObjectConfigCreateInput | AiManagedObjectConfig | null,
): string => stableStringify(normalizeConfig(config));

export const arePanelConfigurationsEqual = (
	left: ManagedBlockConfiguration,
	right: ManagedBlockConfiguration,
	currentPageSourceUrl?: string,
): boolean =>
	stringifyConfig(toAiManagedObjectConfigInput(left, currentPageSourceUrl)) ===
	stringifyConfig(toAiManagedObjectConfigInput(right, currentPageSourceUrl));

export const hasPersistedConfigurationChanged = (
	previousConfig: AiManagedObjectConfig | null | undefined,
	nextConfig: AiManagedObjectConfigCreateInput,
): boolean => {
	if (!previousConfig) {
		return true;
	}

	return stringifyConfig(previousConfig) !== stringifyConfig(nextConfig);
};

export const hasExecutableConfigurationChanged = (
	previousConfig: AiManagedObjectConfig | null | undefined,
	nextConfig: AiManagedObjectConfigCreateInput,
): boolean => {
	if (!previousConfig) {
		return true;
	}

	const previousExecutableConfig = normalizeConfig(previousConfig);
	const nextExecutableConfig = normalizeConfig(nextConfig);
	delete previousExecutableConfig.desiredUpdateFrequency;
	delete nextExecutableConfig.desiredUpdateFrequency;

	return stableStringify(previousExecutableConfig) !== stableStringify(nextExecutableConfig);
};
